/* ------------------------------------------------------------------
   PublicBioPage.js  —  the public link-in-bio page at /u/:username
   Client-side rendered (Phase 1). Fetches /api/bio/public/:username,
   passing ?src=qr through so QR-code visits are counted separately.
------------------------------------------------------------------ */
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { themeToCss } from './bioThemes';
import LatestPosts from './LatestPosts';
import EmailCapture from './EmailCapture';
import { BioIcon, iconFor } from './bioIcons';
import { ContactButton } from './bioContact';

// mailto:/tel:/sms: links open directly (a redirect to them is unreliable
// in some browsers); the click is still counted through /api/bio/go.
const isAppLink = url => /^(mailto|tel|sms):/i.test(String(url || ''));
const countClick = id => {
  try { fetch(`/api/bio/go/${id}`, { redirect: 'manual', keepalive: true }).catch(() => {}); } catch { /* ignore */ }
};

export default function PublicBioPage() {
  const { username } = useParams();
  const { search } = useLocation();
  const fromQr = new URLSearchParams(search).get('src') === 'qr';
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/bio/public/${encodeURIComponent(username)}${fromQr ? '?src=qr' : ''}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'notfound' : 'error');
        const data = await res.json();
        if (alive) setState({ loading: false, error: null, data });
      } catch (e) {
        if (alive) setState({ loading: false, error: e.message, data: null });
      }
    })();
    return () => { alive = false; };
  }, [username, fromQr]);

  const { loading, error, data } = state;

  if (loading) {
    return <CenteredScreen bg="#f8fafc"><div style={{ color: '#9ca3af' }}>Loading…</div></CenteredScreen>;
  }
  if (error || !data) {
    return (
      <CenteredScreen bg="#f8fafc">
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#111827', marginBottom: 8 }}>Page not found</h2>
          <p style={{ color: '#6b7280' }}>No published page exists at <strong>/u/{username}</strong>.</p>
          <a href="/" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>Create yours on Greatly Social →</a>
        </div>
      </CenteredScreen>
    );
  }

  const css = themeToCss(data.theme);
  const initials = (data.displayName || data.username || '?').trim().charAt(0).toUpperCase();

  return (
    <div style={{ minHeight: '100vh', background: css.pageBackground, padding: '48px 18px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 540, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* Avatar */}
        <div style={{
          width: 96, height: 96, borderRadius: '50%', overflow: 'hidden',
          background: css.avatar.background, color: css.avatar.color, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 38, fontWeight: 700, marginBottom: 16,
          border: '2px solid rgba(255,255,255,0.25)',
        }}>
          {data.avatarUrl
            ? <img src={data.avatarUrl} alt={data.displayName || data.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : initials}
        </div>

        {/* Name + bio */}
        <h1 style={{ color: css.textColor, fontSize: 22, fontWeight: 700, margin: '0 0 6px', textAlign: 'center' }}>
          {data.displayName || `@${data.username}`}
        </h1>
        {data.bio && (
          <p style={{ color: css.subColor, fontSize: 15, margin: '0 0 28px', textAlign: 'center', maxWidth: 420 }}>
            {data.bio}
          </p>
        )}

        {/* Contact button + link buttons */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ContactButton contact={data.contact} css={css} />
          {(data.links || []).filter(l => l.type !== 'social').map(link => (
            <a
              key={link.id}
              href={isAppLink(link.url) ? link.url : `/api/bio/go/${link.id}`}
              onClick={isAppLink(link.url) ? () => countClick(link.id) : undefined}
              style={{
                position: 'relative', display: 'block', textAlign: 'center', padding: iconFor(link.icon) ? '15px 44px' : '15px 18px',
                ...css.button, fontWeight: 600, fontSize: 15,
                textDecoration: 'none', transition: 'transform 0.12s ease, opacity 0.12s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.94'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
            >
              {iconFor(link.icon) && <BioIcon name={link.icon} size={18} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)' }} />}
              {link.title}
            </a>
          ))}
        </div>

        {/* Email capture → CRM */}
        {data.collectEmails && (
          <EmailCapture username={data.username} heading={data.emailHeading} buttonText={data.emailButton} css={css} />
        )}

        {/* Latest posts grid (auto-pulled from connected accounts) */}
        <LatestPosts posts={data.posts} textColor={css.textColor} subColor={css.subColor} />

        {/* Footer badge — the acquisition loop */}
        <a
          href="https://greatlysocial.com"
          style={{ marginTop: 40, color: css.subColor, fontSize: 13, textDecoration: 'none', opacity: 0.9 }}
        >
          ⚡ Made with Greatly Social
        </a>
      </div>
    </div>
  );
}

function CenteredScreen({ bg, children }) {
  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {children}
    </div>
  );
}
